import React, { useState } from "react";
import {
  FaFilter,
  FaSearch,
  FaChevronRight,
  FaChevronLeft,
  FaPlus,
} from "react-icons/fa";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { Link } from "react-router-dom";
import Footer from "../../components/footer";

import { useFunctionalChecklistByOrg } from "../../hooks/getFunctionalChecklistByOrg";
import { formatUtcDate } from "../../utils/helper";

const ManageFunctionalChecklists = () => {
  const [statusIcon, setStatusIcon] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Filter state
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // Fetch checklists with pagination
  const { data, isLoading, error } = useFunctionalChecklistByOrg(
    currentPage,
    pageSize
  );

  const statusIconRotate = statusIcon ? "rotate(180deg)" : "rotate(0)";

  // Search handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Filter handlers
  const handleStatusFilter = (statusValue) => {
    setFilterStatus((prev) => {
      if (prev.includes(statusValue)) {
        return prev.filter((s) => s !== statusValue);
      }
      return [...prev, statusValue];
    });
  };

  const clearFilters = () => {
    setFilterStatus([]);
    setFilterDateFrom("");
    setFilterDateTo("");
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    // Close modal
    const modal = document.getElementById("filterModal");
    if (modal) {
      window.$(modal).modal("hide");
    }
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      filterStatus.length > 0 ||
      filterDateFrom.trim() !== "" ||
      filterDateTo.trim() !== ""
    );
  };

  // Get filtered data - server-side filtering applied via API
  const getFilteredData = () => {
    // Return server data directly - no client-side filtering
    return data?.data?.content || [];
  };

  // Get total count from server
  const getTotalFilteredCount = () => {
    return data?.data?.totalElements || 0;
  };

  // Pagination handlers
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(getTotalFilteredCount() / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const formatDate = formatUtcDate;

  // Calculate pagination info from server data
  const totalElements = data?.data?.totalElements || 0;
  const totalPages = data?.data?.totalPages || 1;
  const hasNextPage = !data?.data?.last;
  const hasPreviousPage = !data?.data?.first;

  return (
    <>
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body mb-3 d-flex flex-column h-100">
            <div className="flex-shrink-0">
              <div className="d-flex justify-content-between">
                <h5 className="font-weight-lighter fs-18">
                  Admin: Manage Functional Checklists
                </h5>
                <Link
                  className="btn btn-primary small-text d-flex align-items-center"
                  to="/admin/create_custom_functional_checklist"
                >
                  <FaPlus size={13} className="mr-2" />
                  Create Custom Functional Checklist
                </Link>
              </div>
              <hr />

              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <h5 className="font-weight-lighter mt-2 fs-18">
                    Master Functional Checklists List
                  </h5>
                </div>
                <div
                  className="d-flex align-items-center"
                  style={{ width: 355 }}
                >
                  <div className="input-group bg_muted rounded">
                    <div className="input-group-prepend">
                      <span
                        className="input-group-text border-0 bg-transparent text-muted pr-3"
                        id="basic-addon1"
                      >
                        <FaSearch size={16} color="#c2c2c2" />
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control border-none bg_muted f-13 text-black outline-none rounded mr-3"
                      placeholder="Search by title, ID, status, or date..."
                      aria-label="Username"
                      aria-describedby="basic-addon1"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <button
                    className="btn btn-outline-primary w-50 ml-3 rounded py-1"
                    data-toggle="modal"
                    data-target="#filterModal"
                  >
                    <FaFilter size={16} />
                    &nbsp;&nbsp; <small>Filter</small>
                    {hasActiveFilters() && (
                      <span className="badge badge-success ml-2">
                        {filterStatus.length +
                          (filterDateFrom ? 1 : 0) +
                          (filterDateTo ? 1 : 0)}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Flexible Table Area */}
            <div
              className="flex-grow-1 d-flex flex-column mt-3"
              style={{ minHeight: 0 }}
            >
              <div
                className="flex-grow-1 hide-scrollbar table-responsive"
                style={{
                  overflowY: "auto",
                  scrollbarWidth: "none", // Firefox
                  msOverflowStyle: "none", // IE and Edge
                }}
              >
                <table className="table table-striped table-bordered mb-0 text-center">
                  <thead
                    className="head_light text-white"
                    style={{ position: "sticky", top: 0, zIndex: 10 }}
                  >
                    <tr className="f-11">
                      <th scope="col">Checklist #</th>
                      <th scope="col">Title</th>
                      <th scope="col">Type</th>
                      <th scope="col">Date Created</th>
                      <th scope="col">Status</th>
                      <th scope="col">Next Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filteredData = getFilteredData();
                      return !isLoading && filteredData.length > 0 ? (
                        filteredData.map((item, i) => (
                          <tr key={i}>
                            <th>
                              <small>{i + 1}</small>
                            </th>
                            <td>
                              <small>{item.name}</small>
                            </td>
                            <td>
                              <small>Custom</small>
                            </td>
                            <td>
                              <small>{formatDate(item.dateCreated)}</small>
                            </td>
                            <td>
                              <small>
                                <span
                                  className={`badge ${
                                    item.status === "PUBLISHED"
                                      ? "badge-success"
                                      : "badge-warning"
                                  }`}
                                >
                                  {item.status}
                                </span>
                              </small>
                            </td>
                            <td>
                              <Link
                                to={`/admin/view_checklist`}
                                onClick={() =>
                                  localStorage.setItem("checklist_id", item.id)
                                }
                                className="border_success rounded px-4"
                              >
                                <i className="fa fa-chevron-right" />
                              </Link>
                            </td>
                          </tr>
                        ))
                      ) : !isLoading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <small className="text-muted">
                              {searchTerm.trim()
                                ? `No checklists found matching "${searchTerm}"`
                                : "No checklists found"}
                            </small>
                          </td>
                        </tr>
                      ) : null;
                    })()}
                    {isLoading &&
                      // Loading skeleton rows
                      [...Array(8)].map((_, index) => (
                        <tr key={`skeleton-${index}`}>
                          <th>
                            <small>Loading...</small>
                          </th>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                        </tr>
                      ))}
                    {!isLoading &&
                      (!data?.data?.content ||
                        data.data.content.length === 0) && (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <small className="text-muted">
                              No checklists found
                            </small>
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>

              {/* Fixed Pagination Controls - Bottom of card */}
              {!isLoading && data?.data && data.data.content.length > 0 && (
                <div className="flex-shrink-0 d-flex justify-content-between align-items-center mt-4 flex-wrap mobile-stack">
                  {/* Page Size Dropdown - Bottom Left */}
                  <div className="d-flex align-items-center mb-2 mb-md-0 mobile-text-center">
                    <span
                      className="text-dark mr-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      Show
                    </span>
                    <select
                      className="border rounded"
                      style={{
                        width: "50px",
                        height: "24px",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "#495057",
                        backgroundColor: "#fff",
                        border: "1px solid #ced4da",
                        borderRadius: "6px",
                        padding: "2px 4px",
                        outline: "none",
                      }}
                      value={pageSize}
                      onChange={(e) =>
                        handlePageSizeChange(parseInt(e.target.value))
                      }
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                      <option value={25}>25</option>
                    </select>
                    <span
                      className="text-dark ml-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      entries
                    </span>
                  </div>

                  {/* Pagination Navigation - Bottom Right */}
                  <div className="d-flex align-items-center flex-column flex-md-row">
                    <span
                      className="text-muted mr-0 mr-md-4 mb-2 mb-md-0"
                      style={{ fontSize: "12px" }}
                    >
                      {totalElements > 0
                        ? `Showing ${Math.min(
                            (currentPage - 1) * pageSize + 1,
                            totalElements
                          )} to ${Math.min(
                            currentPage * pageSize,
                            totalElements
                          )} of ${totalElements} entries`
                        : "No entries to show"}
                    </span>

                    {totalPages > 1 && (
                      <nav aria-label="Page navigation">
                        <div className="d-flex align-items-center pagination-mobile">
                          {/* Previous Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center mr-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasPreviousPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasPreviousPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasPreviousPage
                                ? "not-allowed"
                                : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handlePreviousPage}
                            disabled={!hasPreviousPage}
                          >
                            <FaChevronLeft size={12} />
                          </button>

                          {/* Page X of Y Display */}
                          <div className="d-flex align-items-center mx-2">
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#495057",
                              }}
                            >
                              Page {currentPage} of {totalPages}
                            </span>
                          </div>

                          {/* Next Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center ml-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasNextPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasNextPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasNextPage ? "not-allowed" : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handleNextPage}
                            disabled={!hasNextPage}
                          >
                            <FaChevronRight size={12} />
                          </button>
                        </div>
                      </nav>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal fade"
        id="filterModal"
        tabindex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog" style={{ width: "395px" }}>
          <div
            className="modal-content pt-3 border-0"
            style={{ borderRadius: "10px" }}
          >
            <div className="modal-header border-0">
              <div className="flex-grow-1 text-center">
                <h5 className="text_primary" style={{ fontWeight: 700 }}>
                  Search Filter
                </h5>
              </div>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 33, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div className="modal-body px-3">
              {/* Status Filter */}
              <div className="mb-4">
                <label className="small-text font-weight-bold mb-2">
                  Filter by Status:
                </label>
                <div className="dropdown">
                  <button
                    className="btn btn-block border text-left overlay-dropdown-input"
                    type="button"
                    id="dropdownStatusButton"
                    data-toggle="dropdown"
                    data-display="static"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClickCapture={() => setStatusIcon(!statusIcon)}
                  >
                    <div className="d-flex justify-content-between">
                      <small className="small-text">
                        {filterStatus.length > 0
                          ? `${filterStatus.length} status(es) selected`
                          : "Choose Status"}
                      </small>
                      <BsChevronDown
                        style={{
                          transform: statusIconRotate,
                          transition: "all 0.3s linear",
                        }}
                      />
                    </div>
                  </button>
                  <div
                    className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                    aria-labelledby="dropdownStatusButton"
                  >
                    <p className="pl-2 mb-0 pb-2">
                      <small className="small-text">
                        Choose as many as applicable:
                      </small>
                    </p>
                    {["PUBLISHED", "DRAFT", "ARCHIVED"].map((statusValue) => (
                      <div
                        className="dropdown-item noFilterHover form-group mb-0 form-check ml-4 wa nohover h-38"
                        key={statusValue}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-2"
                          id={`status-${statusValue}`}
                          checked={filterStatus.includes(statusValue)}
                          onChange={() => handleStatusFilter(statusValue)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`status-${statusValue}`}
                        >
                          <small className="small-text">{statusValue}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Date Range Filter */}
              <div className="mb-4">
                <label className="small-text font-weight-bold mb-2">
                  Filter by Date Range:
                </label>
                <div className="row">
                  <div className="col-6">
                    <label className="small-text mb-1">From Date:</label>
                    <input
                      type="date"
                      className="form-control small-text"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      max={filterDateTo || undefined}
                    />
                  </div>
                  <div className="col-6">
                    <label className="small-text mb-1">To Date:</label>
                    <input
                      type="date"
                      className="form-control small-text"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      min={filterDateFrom || undefined}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center border-0">
              <button
                type="button"
                className="btn btn_outline_danger overlay-btn"
                onClick={clearFilters}
              >
                CLEAR
              </button>
              <button
                type="button"
                className="btn btn-outline-primary overlay-btn"
                onClick={applyFilters}
              >
                APPLY
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ManageFunctionalChecklists;
